/**
 * ShiftLineItems - Phase 3 Component
 * 
 * Displays and manages ShiftTaskLineItems for a shift:
 * - Primary Line section (readonly display)
 * - Associated Costs section (collapsible, editable)
 * - Live computed totals
 */
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from "react";
import { 
  getShiftLineItems, 
  updateShiftLineItem, 
  deleteShiftLineItem,
  createShiftLineItem,
  getTaskDefinitions,
  getTaskCategories
} from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { 
  ChevronDown, 
  ChevronRight, 
  Trash2, 
  Plus,
  DollarSign,
  Loader2,
  AlertCircle,
  Sparkles,
  PenLine
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

export const ShiftLineItems = forwardRef(function ShiftLineItems({ 
  shiftId, 
  crewCount = 1,
  durationHours = 0,
  onTotalsChange = () => {} 
}, parentRef) {
  const [lineItems, setLineItems] = useState([]);
  const [summary, setSummary] = useState({ total_charge: 0, primary_total: 0, supplement_total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [costsOpen, setCostsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [availableSupplements, setAvailableSupplements] = useState([]);
  const [selectedSupplement, setSelectedSupplement] = useState("");
  const [addQuantity, setAddQuantity] = useState(1);
  const [addLoading, setAddLoading] = useState(false);

  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
  // Server-side inverse-undo log. Every mutation made through this
  // component during a parent-modal session is recorded so the parent
  // can call `discard()` on X/Cancel to walk the log in reverse and
  // restore the original persisted state.
  //
  // Log shape, each entry:
  //   { op: "create", id }                      — to undo: deleteShiftLineItem(id)
  //   { op: "delete", snapshot }                — to undo: createShiftLineItem(...snapshot)
  //   { op: "update", id, originalFields:{} }   — to undo: updateShiftLineItem(id, originalFields)
  const mutationLogRef = useRef([]);
  // Snapshot of the line item rows when the component first loaded data.
  // Used as a defensive "second source of truth" if the log misses an op.
  const originalSnapshotRef = useRef(null);
  
  // EF-SHIFT-PRIMARY-ROLE-SELECTION: State for primary role selection
  const [availablePrimaryRoles, setAvailablePrimaryRoles] = useState([]);
  const [primaryRoleLoading, setPrimaryRoleLoading] = useState(false);

  // Load line items
  // EF-SHIFT-LINE-ITEM-STABILITY-FIX-V1: Robust error handling with safe fallback
  const loadLineItems = useCallback(async () => {
    if (!shiftId) return;
    
    try {
      setLoading(true);
      const response = await getShiftLineItems(shiftId);
      const items = response.data?.items || [];
      const summaryData = response.data?.summary || { total_charge: 0, primary_total: 0, supplement_total: 0, item_count: 0 };
      
      setLineItems(items);
      setSummary(summaryData);
      onTotalsChange(summaryData);

      // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
      // Take a snapshot of the persisted line items the FIRST time we
      // successfully load them for this shift. The snapshot is the
      // "known good" state to restore to on parent-discard.
      if (originalSnapshotRef.current === null) {
        originalSnapshotRef.current = items.map((it) => ({ ...it }));
      }
      
      console.log(`[LINE ITEMS LOAD] shift_id=${shiftId}, items_found=${items.length}`);
    } catch (err) {
      // EF-SHIFT-LINE-ITEM-STABILITY-FIX-V1: Safe fallback - never crash
      console.error("[LINE ITEMS LOAD FAILED]", err);
      setLineItems([]);  // SAFE FALLBACK
      setSummary({ total_charge: 0, primary_total: 0, supplement_total: 0, item_count: 0 });
      // Don't show toast on load failure - might be intentional (deleted shift)
    } finally {
      setLoading(false);
    }
  }, [shiftId, onTotalsChange]);

  useEffect(() => {
    loadLineItems();
  }, [loadLineItems]);

  // Load available supplements for adding
  // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
  // Replaced the hardcoded phantom SUPPLEMENT_CATEGORY_ID with a LIVE
  // lookup by category key. The previous constant
  // "ce964e83-101d-49fe-8603-ae50b8898c33" no longer matches any row
  // in task_categories (the live SUPPLEMENT category id is currently
  // f911559f-…). We ALSO include orphan-categorised task defs whose
  // own `task_type` is item / supplement / allowance so production
  // task defs like "Prime Poids-Lourd" (parented to a deleted
  // category) still show up in the Add Supplement dropdown.
  const SUPPLEMENT_CATEGORY_KEY = "SUPPLEMENT";
  const ITEM_CATEGORY_KEY = "ITEM";
  // EVENTSFLO-SHIFT-ROLE-DROPDOWN-EMERGENCY-STABILISATION-V4
  // Legacy phantom STAFFING category id. After the V1 normalisation
  // migration all 15 active crew roles were moved OUT of this id into
  // the live ROLE category. We keep the constant ONLY so we can include
  // the currently-selected historical role even when it still references
  // this phantom category (backward-compatibility safety net).
  const LEGACY_STAFFING_CATEGORY_ID = "8b7327f0-89f4-4298-b7d8-24c5272653cc";

  const loadAvailableSupplements = async () => {
    try {
      // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
      // Only show enabled task definitions in new-shift supplement selector.
      const [defsRes, catsRes] = await Promise.all([
        getTaskDefinitions({ limit: 10000, enabled_filter: "enabled" }),
        getTaskCategories(),
      ]);
      const allTasks = defsRes.data?.items || defsRes.data || [];
      const allCats = catsRes.data?.items || catsRes.data || [];
      // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
      // Live SUPPLEMENT + ITEM category id resolution.
      const supplementCatIds = new Set(
        (allCats || [])
          .filter((c) => {
            const k = (c?.key || "").toUpperCase();
            return k === SUPPLEMENT_CATEGORY_KEY || k === ITEM_CATEGORY_KEY;
          })
          .map((c) => c.id),
      );
      const liveCatIds = new Set((allCats || []).map((c) => c.id));
      const supplements = allTasks.filter((t) => {
        if (!t.is_active) return false;
        // Path 1: task def lives in a live SUPPLEMENT or ITEM category.
        if (t.category_id && supplementCatIds.has(t.category_id)) return true;
        // Path 2: orphan category fallback — the stored category_id does
        // not match any live category, but the task def's own `task_type`
        // says it is a supplement / item / allowance. Surface it so it
        // can still be added during a planning session.
        const tt = (t.task_type || "").toLowerCase();
        if (
          t.category_id
          && !liveCatIds.has(t.category_id)
          && (tt === "item" || tt === "supplement" || tt === "allowance")
        ) {
          return true;
        }
        return false;
      });
      setAvailableSupplements(supplements);
    } catch (err) {
      console.error("Failed to load supplements:", err);
    }
  };

  // EF-SHIFT-PRIMARY-ROLE-SELECTION: Load available primary roles
  // EVENTSFLO-SHIFT-ROLE-DROPDOWN-EMERGENCY-STABILISATION-V4
  //   Root cause of empty Primary Role dropdown: previous code hardcoded
  //   the LEGACY_STAFFING_CATEGORY_ID. After the V1 role-catalogue
  //   normalisation, that category no longer contains any crew roles —
  //   they all moved to the live ROLE category.
  //
  //   Robust rule (matches V3/V4 spec): resolve the live ROLE category
  //   dynamically via /api/task-categories and filter task definitions
  //   by category key === "ROLE" + is_active. Always include the
  //   currently-selected primary role even when it still references the
  //   legacy phantom category, so historical shifts keep their saved
  //   selection.
  const loadAvailablePrimaryRoles = useCallback(async () => {
    try {
      const [defsRes, catsRes] = await Promise.all([
        getTaskDefinitions({ limit: 10000, enabled_filter: "enabled" }),
        getTaskCategories(),
      ]);
      const allTasks = defsRes.data?.items || defsRes.data || [];
      const allCats = catsRes.data?.items || catsRes.data || [];
      const roleCat = (allCats || []).find(
        (c) => (c?.key || "").toUpperCase() === "ROLE",
      );
      const roleCategoryId = roleCat?.id || null;

      const primaryRoles = (allTasks || []).filter(
        (t) => t.is_active !== false && roleCategoryId && t.category_id === roleCategoryId,
      );

      // Backward-compatibility safety net: ensure currently-selected
      // primary role(s) stay visible even when they reference the legacy
      // phantom STAFFING category or any other non-ROLE category.
      const selectedIds = new Set(
        (lineItems || [])
          .filter((li) => li.type_snapshot === "primary")
          .map((li) => li.task_definition_id)
          .filter(Boolean),
      );
      for (const sid of selectedIds) {
        if (!primaryRoles.find((r) => r.id === sid)) {
          const found = (allTasks || []).find((t) => t.id === sid);
          if (found) primaryRoles.push(found);
        }
      }

      // Sort: 'Crew' first (canonical default), then alphabetical.
      primaryRoles.sort((a, b) => {
        if (a.name === "Crew") return -1;
        if (b.name === "Crew") return 1;
        return (a.name || "").localeCompare(b.name || "");
      });

      setAvailablePrimaryRoles(primaryRoles);
    } catch (err) {
      console.error("Failed to load primary roles:", err);
    }
  }, [lineItems]);

  useEffect(() => {
    loadAvailablePrimaryRoles();
  }, [loadAvailablePrimaryRoles]);

  // EF-SHIFT-PRIMARY-ROLE-SELECTION: Update primary role (UPDATE, not recreate)
  const handlePrimaryRoleChange = async (lineItemId, newTaskDefinitionId) => {
    // Find the selected task definition to get its details
    const selectedRole = availablePrimaryRoles.find(r => r.id === newTaskDefinitionId);
    if (!selectedRole) return;

    setPrimaryRoleLoading(true);
    try {
      // UPDATE the existing line item - preserves the same ID
      await updateShiftLineItem(lineItemId, {
        task_definition_id: newTaskDefinitionId,
        name_snapshot: selectedRole.name,
        unit_rate: selectedRole.default_charge_rate || 0
      });
      
      // Reload to get updated data
      await loadLineItems();
      toast.success(`Primary role updated to ${selectedRole.name}`);
    } catch (err) {
      console.error("Failed to update primary role:", err);
      toast.error("Failed to update primary role");
    } finally {
      setPrimaryRoleLoading(false);
    }
  };

  // Get primary items and supplement items
  // EF-SHIFT-TASK-NORMALISATION-LOCK-V1: Filter out invalid data (qty <= 0 or rate <= 0)
  const primaryItems = lineItems.filter(item => item.type_snapshot === 'primary');
  const supplementItems = lineItems.filter(item => {
    if (item.type_snapshot !== 'supplement') return false;
    // Hide items with qty <= 0 (except toll which can have qty=0)
    const isToll = (item.name_snapshot || "").toLowerCase().includes("toll") || 
                   (item.supplement_group_snapshot || "").toLowerCase() === "toll";
    if ((item.quantity === null || item.quantity <= 0) && !isToll) return false;
    // Hide items with rate < 0
    if (item.unit_rate === null || item.unit_rate < 0) return false;
    return true;
  });
  
  // EF-SHIFT-TASK-NORMALISATION-LOCK-V1: Helper to check if item is Meal Allowance (read-only)
  const isMealAllowance = (item) => {
    const nameLower = (item.name_snapshot || "").toLowerCase();
    const groupLower = (item.supplement_group_snapshot || "").toLowerCase();
    return nameLower.includes("meal") || nameLower.includes("repas") || groupLower === "meal_allowance";
  };
  
  // EF-SHIFT-TASK-NORMALISATION-LOCK-V1: Get existing task_definition_ids for duplicate prevention
  const existingTaskDefIds = new Set(lineItems.map(item => item.task_definition_id).filter(Boolean));

  // EF-SHIFT-FOOTER-SOURCE-CONSOLIDATION:
  // SINGLE SOURCE OF TRUTH: Derive all totals from `lineItems` only.
  // This is the same normalized dataset used to render the ShiftLineItems table
  // and aligns with Quote generation logic.
  
  // EF-CALCULATION-CONSISTENCY-ENFORCEMENT-V1: Use computed_total from backend (single source of truth)
  // DO NOT recalculate in frontend - use stored values only
  const primaryTotal = primaryItems.reduce((acc, item) => {
    // Use backend computed_total (which comes from stored line_total)
    return acc + (item.computed_total || 0);
  }, 0);
  
  // Associated total: Sum of all supplement line items using their computed_total
  const associatedTotal = supplementItems.reduce((acc, item) => {
    return acc + (item.computed_total || 0);
  }, 0);
  
  // Shift total: Primary + Associated (from lineItems only)
  const shiftTotal = primaryTotal + associatedTotal;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-EU', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Handle updating a line item
  const handleUpdateItem = async (itemId, field, value) => {
    try {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return;

      // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
      // Capture the field's CURRENT value BEFORE the patch so that
      // discard() can restore it. Coalesce sequential edits on the
      // same field into a single log entry (the very first original
      // value is the one we want to restore to).
      const before = lineItems.find((it) => it.id === itemId);
      if (before && before[field] !== numValue) {
        const existing = mutationLogRef.current.find(
          (e) => e.op === "update" && e.id === itemId && (field in e.originalFields),
        );
        if (!existing) {
          // record original value of just this field
          mutationLogRef.current.push({
            op: "update",
            id: itemId,
            originalFields: { [field]: before[field] },
          });
        }
      }

      await updateShiftLineItem(itemId, { [field]: numValue });
      await loadLineItems();
    } catch (err) {
      toast.error("Failed to update item");
    }
  };

  // Handle deleting a line item
  // EF-SHIFT-LINE-ITEM-STABILITY-FIX-V1: Robust delete with full refresh
  const handleDeleteItem = async (itemId, itemName) => {
    // EVENTSFLO-SHIFT-RULE-MANUAL-REMOVAL-SUPPRESSION-FIX-V1
    // Surface the suppression policy to the admin so deleting a
    // rule-managed supplement isn't surprising. Plain-language only,
    // no extra dialogs — the backend handles the bookkeeping.
    const snapshotEarly = lineItems.find((it) => it.id === itemId);
    if (snapshotEarly?.rule_added && !snapshotEarly?.is_overridden) {
      const ok = window.confirm(
        `Remove "${itemName}"?\n\n` +
        `This is a rule-managed supplement. Removing it will be ` +
        `treated as a manual override for this shift — the rule will ` +
        `not silently re-add it on the next save. You can add it back ` +
        `manually at any time to clear the override.`
      );
      if (!ok) return;
    }
    try {
      // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
      // Capture the FULL row before deleting so discard() can recreate
      // it with the same task_definition_id + quantity + rate.
      const snapshot = snapshotEarly;
      if (snapshot) {
        mutationLogRef.current.push({ op: "delete", snapshot: { ...snapshot } });
      }

      await deleteShiftLineItem(itemId);
      toast.success(`Removed "${itemName}"`);

      // EF-SHIFT-LINE-ITEM-STABILITY-FIX-V1: FULL REFRESH FROM API (not local state mutation)
      await loadLineItems();
      console.log(`[LINE ITEM DELETE] id=${itemId} completed, refreshed list`);
    } catch (err) {
      console.error("[LINE ITEM DELETE FAILED]", err);
      // Even if delete API fails, try to refresh to get current state
      await loadLineItems();
      // Only show error if the item still exists after refresh
      const stillExists = lineItems.some(item => item.id === itemId);
      if (stillExists) {
        toast.error("Failed to remove item");
      } else {
        toast.success(`Removed "${itemName}"`);
      }
    }
  };

  // Handle adding a new supplement
  const handleAddSupplement = async () => {
    if (!selectedSupplement) return;

    setAddLoading(true);
    try {
      // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
      // The new row's id only exists AFTER the API returns. We read it
      // back from the next loadLineItems() and patch the log entry's id.
      const beforeIds = new Set(lineItems.map((it) => it.id));
      // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
      // Send explicit type_snapshot so the backend cannot fall back to
      // PRIMARY when the task def's category_id is orphaned (e.g.
      // "Prime Poids-Lourd" lives in a phantom category that no longer
      // exists in task_categories). Without this the row was classified
      // as PRIMARY and rendered with crewCount as the quantity.
      const resp = await createShiftLineItem({
        shift_id: shiftId,
        task_definition_id: selectedSupplement,
        quantity: parseFloat(addQuantity) || 1,
        type_snapshot: "supplement",
      });
      // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
      // Push the mutation-log entry BEFORE closing the inner dialog.
      // Closing the dialog triggers Radix to dispatch pointerDownOutside
      // to the outer shift dialog whose onOpenChange interrogates
      // hasChanges(). Previously the log entry was pushed AFTER
      // setAddDialogOpen(false) which left hasChanges() returning false
      // for a single render → the outer dialog interpreted the close
      // as a clean cancel and navigated back to the project screen.
      const earlyId = resp?.data?.id || resp?.id || null;
      mutationLogRef.current.push({
        op: "create",
        id: earlyId,
        pending_id_resolution: !earlyId,
      });
      toast.success("Supplement added");
      setAddDialogOpen(false);
      setSelectedSupplement("");
      setAddQuantity(1);
      await loadLineItems();
      // Resolve id if it wasn't on the response (set-diff fallback).
      if (!earlyId) {
        const afterIds = (await getShiftLineItems(shiftId))?.data?.items || [];
        const added = afterIds.find((it) => !beforeIds.has(it.id));
        const resolved = added?.id;
        if (resolved) {
          const entry = mutationLogRef.current[
            mutationLogRef.current.length - 1
          ];
          if (entry && entry.pending_id_resolution) {
            entry.id = resolved;
            entry.pending_id_resolution = false;
          }
        }
      }
    } catch (err) {
      toast.error("Failed to add supplement");
    } finally {
      setAddLoading(false);
    }
  };

  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
  // Imperative API for the parent shift modal. Parent owns Save/X buttons:
  //   - hasChanges():    boolean — true if user touched line items
  //                       since the modal opened.
  //   - commit():        no-op on success path. Just clears the log so
  //                       a subsequent X/Cancel cannot undo committed
  //                       Save state.
  //   - discard():       walk the log in reverse and inverse-apply each
  //                       mutation, then reload.
  useImperativeHandle(parentRef, () => ({
    hasChanges: () => mutationLogRef.current.length > 0,
    pendingChangeCount: () => mutationLogRef.current.length,
    commit: () => {
      // Save path — accept current persisted state as the new baseline.
      mutationLogRef.current = [];
      originalSnapshotRef.current = lineItems.map((it) => ({ ...it }));
    },
    discard: async () => {
      const log = [...mutationLogRef.current].reverse();
      let failures = 0;
      for (const entry of log) {
        try {
          if (entry.op === "create") {
            await deleteShiftLineItem(entry.id);
          } else if (entry.op === "delete") {
            const s = entry.snapshot || {};
            await createShiftLineItem({
              shift_id: shiftId,
              task_definition_id: s.task_definition_id,
              quantity: s.quantity ?? 1,
              unit_rate: s.unit_rate,
              unit_rate_pay: s.unit_rate_pay,
              charge_rate: s.charge_rate,
              pay_rate: s.pay_rate,
              notes: s.notes,
            });
          } else if (entry.op === "update") {
            await updateShiftLineItem(entry.id, entry.originalFields || {});
          }
        } catch (e) {
          failures += 1;
          console.error("[ShiftLineItems.discard] inverse-op failed", entry, e);
        }
      }
      // Clear log + refresh.
      mutationLogRef.current = [];
      await loadLineItems();
      return { restored: log.length - failures, failures };
    },
  }), [lineItems, shiftId, loadLineItems]);

  if (loading && lineItems.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading costs...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Primary Line Section - EF-SHIFT-PRIMARY-ROLE-SELECTION */}
      {primaryItems.length > 0 && (
        <div className="bg-surface-elevated rounded-lg p-4 border border-border/50">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-primary" />
            Primary Task
          </h4>
          {primaryItems.map((item) => {
            // EF-SHIFT-FOOTER-TOTALS-REGRESSION-FIX: Use current form values
            // EVENTSFLO-SHIFT-EDIT-STATE-CONSISTENCY-AND-PDF-LOGO-SIZE-V1
            // Source of truth while the Edit Shift modal is open is the
            // LIVE form state, not the persisted line-item snapshot. The
            // previous priority (item.duration_hours || durationHours)
            // showed stale 8h when the user lowered duration to 4h before
            // saving — producing the user-reported "top says 4h, bottom
            // says 8h" inconsistency. Mirror the same form-first rule we
            // already use for crew quantity below.
            const itemDuration = durationHours > 0 ? durationHours : (item.duration_hours || 0);
            // Use crewCount from form (not stale item.quantity)
            const effectiveQuantity = crewCount || item.quantity || 1;
            const correctedItemTotal = effectiveQuantity * item.unit_rate * itemDuration;
            return (
              <div key={item.id} className="space-y-3">
                {/* Role Selection Row */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">Role:</Label>
                  <Select
                    value={item.task_definition_id}
                    onValueChange={(newId) => handlePrimaryRoleChange(item.id, newId)}
                    disabled={primaryRoleLoading}
                  >
                    <SelectTrigger 
                      className="w-[200px] h-8 text-sm"
                      data-testid="primary-role-select"
                    >
                      <SelectValue placeholder="Select role">
                        {primaryRoleLoading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Updating...
                          </span>
                        ) : (
                          item.name_snapshot
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availablePrimaryRoles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center justify-between gap-4">
                            <span>{role.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatCurrency(role.default_charge_rate || 0)}/hr
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">Qty: {effectiveQuantity}</span>
                </div>
                {/* Pricing Row */}
                <div className="grid grid-cols-3 gap-3 items-center text-sm pl-12">
                  <div className="text-muted-foreground">
                    {formatCurrency(item.unit_rate)}/unit × {effectiveQuantity} × {itemDuration}h
                  </div>
                  <div></div>
                  <div className="text-right font-semibold text-foreground">
                    {formatCurrency(correctedItemTotal)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Associated Costs Section (Collapsible) */}
      <Collapsible open={costsOpen} onOpenChange={setCostsOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-3 bg-surface-base rounded-lg border border-border/50 hover:bg-surface-elevated transition-colors">
            <div className="flex items-center gap-2">
              {costsOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium text-foreground">
                Associated Costs ({supplementItems.length} items)
              </span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              {formatCurrency(associatedTotal)}
            </span>
          </button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          <div className="bg-surface-base rounded-lg border border-border/50 overflow-hidden">
            {supplementItems.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                <AlertCircle className="h-5 w-5 mx-auto mb-2 opacity-50" />
                No associated costs
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-surface-elevated/30">
                  <tr className="border-b border-border/50">
                    <th className="text-left px-3 py-2 font-medium text-foreground">Name</th>
                    {/* EF-SHIFT-RULES-NORMALISATION-V2: Show contextual calculation format */}
                    <th className="text-left px-2 py-2 font-medium text-foreground">Calculation</th>
                    <th className="text-right px-3 py-2 font-medium text-foreground w-24">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {supplementItems.map((item) => (
                    <tr key={item.id} className="border-b border-border/30 hover:bg-surface-elevated/20">
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-foreground">{item.name_snapshot}</span>
                            {item.supplement_group_snapshot && (
                              <span className="text-xs text-muted-foreground">
                                ({item.supplement_group_snapshot})
                              </span>
                            )}
                          </div>
                          {/* EVENTSFLO-SHIFT-RULE-VISIBILITY-AND-CREW-PLANNER-ENFORCEMENT-AUDIT-V1
                              Single consolidated provenance badge:
                                Rule-managed   — rule added it, untouched
                                Manual override — rule added it, then user edited
                                Manual          — admin added by hand (no rule)
                          */}
                          <div className="flex items-center gap-2">
                            {item.rule_added && !item.is_overridden && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 border-primary/50 text-primary bg-primary/5 cursor-help"
                                      data-testid={`line-item-provenance-${item.id}`}
                                      data-provenance="rule-managed"
                                    >
                                      <Sparkles className="h-2.5 w-2.5 mr-1" />
                                      Rule-managed
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-surface-elevated border-border">
                                    <p className="text-xs">
                                      <span className="text-muted-foreground">Added by rule: </span>
                                      <span className="font-mono text-foreground">
                                        {item.rule_name || item.rule_id || "System Rule"}
                                      </span>
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {item.rule_added && item.is_overridden && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 border-sky-500/50 text-sky-600 bg-sky-500/5 cursor-help"
                                      data-testid={`line-item-provenance-${item.id}`}
                                      data-provenance="manual-override"
                                    >
                                      <PenLine className="h-2.5 w-2.5 mr-1" />
                                      Manual override
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-surface-elevated border-border">
                                    <p className="text-xs text-foreground">
                                      Rule-managed item the admin has manually edited. Rule recalculations will leave it alone.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {!item.rule_added && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-4 border-muted-foreground/30 text-muted-foreground bg-transparent cursor-help"
                                      data-testid={`line-item-provenance-${item.id}`}
                                      data-provenance="manual"
                                    >
                                      Manual
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-surface-elevated border-border">
                                    <p className="text-xs text-foreground">
                                      Added manually by the admin. Not managed by any rule.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* EF-SHIFT-RULES-NORMALISATION-V2: Contextual calculation display */}
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-1">
                          {/* Show display_format if available (new normalised format) */}
                          {item.display_format ? (
                            <span className="text-sm text-foreground font-mono">
                              {item.display_format}
                            </span>
                          ) : (
                            /* Legacy fallback: show editable qty × rate */
                            <div className="flex items-center gap-2">
                              {isMealAllowance(item) ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="h-7 w-16 flex items-center justify-center text-foreground bg-surface-base border border-border/50 rounded-md cursor-not-allowed opacity-75">
                                        {item.quantity}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="bg-surface-elevated border-border">
                                      <p className="text-xs text-foreground">
                                        Auto-calculated from crew count ({crewCount})
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                                  className="h-7 w-16 text-center text-foreground bg-surface-input"
                                  data-testid={`line-item-qty-${item.id}`}
                                />
                              )}
                              <span className="text-muted-foreground">×</span>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_rate}
                                onChange={(e) => handleUpdateItem(item.id, 'unit_rate', e.target.value)}
                                className="h-7 w-20 text-center text-foreground bg-surface-input"
                                data-testid={`line-item-rate-${item.id}`}
                              />
                            </div>
                          )}
                          {/* Show formula badge for normalised items */}
                          {item.uses_normalised_formula && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-3 w-fit border-green-500/50 text-green-600 bg-green-500/5">
                              Normalised
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-foreground">
                        {formatCurrency(item.computed_total)}
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id, item.name_snapshot)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`delete-line-item-${item.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {/* Add Supplement Button */}
            <div className="p-2 border-t border-border/50 bg-surface-elevated/20">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  // EVENTSFLO-SHIFT-SUPPLEMENT-HOURLY-CALC-EDIT-STABILITY-V2
                  // The parent ProjectDetail Edit-Shift dialog renders
                  // its content inside a <form>. Shadcn's <Button>
                  // does NOT default to type="button" — without an
                  // explicit type the browser treats this as a submit
                  // button, which closes the dialog by saving the
                  // shift. We set type="button" AND defensively call
                  // preventDefault on the click event to neutralise
                  // any remaining bubbling form-submit path.
                  if (e && typeof e.preventDefault === "function") {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  loadAvailableSupplements();
                  setAddDialogOpen(true);
                }}
                className="w-full h-8 text-xs text-muted-foreground hover:text-foreground"
                data-testid="add-supplement-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Supplement
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Totals Summary */}
      <div className="bg-surface-elevated rounded-lg p-4 border border-primary/30">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Primary Total</span>
            <span className="text-foreground">{formatCurrency(primaryTotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Associated Costs</span>
            <span className="text-foreground">{formatCurrency(associatedTotal)}</span>
          </div>
          <div className="border-t border-border/50 pt-2 mt-2">
            <div className="flex justify-between font-semibold">
              <span className="text-foreground">Shift Total</span>
              <span className="text-primary text-lg">{formatCurrency(shiftTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Supplement Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Supplement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Supplement *</Label>
              <Select
                value={selectedSupplement}
                onValueChange={(value) => {
                  setSelectedSupplement(value);
                  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
                  // Auto-prefill quantity for Meal Allowance to match the
                  // crew count (planning quantity). Backend RULE 4 will
                  // also enforce this, but pre-filling avoids showing a
                  // misleading "1" to the planner.
                  const picked = availableSupplements.find((s) => s.id === value);
                  if (picked) {
                    const groupLower = (picked.supplement_group || "").toLowerCase();
                    const nameLower = (picked.name || "").toLowerCase();
                    const isMeal =
                      nameLower.includes("meal") ||
                      nameLower.includes("repas") ||
                      groupLower === "meal" ||
                      groupLower === "meal_allowance";
                    if (isMeal) setAddQuantity(crewCount || 1);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplement" />
                </SelectTrigger>
                <SelectContent>
                  {/* EF-SHIFT-TASK-NORMALISATION-LOCK-V1: Filter out already-added supplements */}
                  {availableSupplements
                    .filter(supp => !existingTaskDefIds.has(supp.id))
                    .map((supp) => (
                      <SelectItem key={supp.id} value={supp.id}>
                        {supp.name}
                        {supp.supplement_group && (
                          <span className="text-muted-foreground ml-2">
                            ({supp.supplement_group})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  {/* Show message if all supplements already added */}
                  {availableSupplements.filter(supp => !existingTaskDefIds.has(supp.id)).length === 0 && (
                    <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                      All available supplements already added
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={addQuantity}
                onChange={(e) => setAddQuantity(e.target.value)}
                data-testid="add-supplement-quantity-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} data-testid="add-supplement-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddSupplement}
              disabled={!selectedSupplement || addLoading}
              data-testid="add-supplement-confirm-btn"
            >
              {addLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
